import React, { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Sparkles, Copy, Check, RefreshCcw, Eraser, Cpu, Send, User, Bot } from 'lucide-react';
import { adminAiReply, adminAiInfo } from '../api';

// claude-haiku-4-5-20251001 → "Claude Haiku 4.5"
const prettyModel = (id) => {
  if (!id) return '—';
  const m = id.match(/claude-(opus|sonnet|haiku)-(\d+)-(\d+)/i);
  if (!m) return id;
  const tier = m[1][0].toUpperCase() + m[1].slice(1);
  return `Claude ${tier} ${m[2]}.${m[3]}`;
};

const EXAMPLES = [
  '¿Me pasas tu catálogo?',
  '¿Tienes una laptop gamer i7?',
  'Busco laptop para oficina con pantalla OLED',
  '¿Tienes Asus TUF?',
  '¿Qué laptop me recomiendas de S/3500 gamer?',
];

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      onClick={onCopy}
      className="flex items-center gap-1.5 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.1em] border border-brand-border-md text-brand-muted hover:border-brand-accent hover:text-brand-accent transition-colors"
    >
      {copied
        ? <><Check size={11} /> Copiado</>
        : <><Copy size={11} /> Copiar</>}
    </button>
  );
};

const AIAssistantPage = () => {
  const [history, setHistory] = useState([]); // [{role, content, meta?}]
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [totalCost, setTotalCost] = useState({ usd: 0, pen: 0 });
  const taRef = useRef(null);
  const scrollRef = useRef(null);

  const { data: info } = useQuery({
    queryKey: ['admin-ai-info'],
    queryFn: adminAiInfo,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => { taRef.current?.focus(); }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [history, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;

    // Optimistic: agrega el mensaje del cliente al historial inmediatamente
    const newUserMsg = { role: 'user', content: text };
    const nextHistory = [...history, newUserMsg];
    setHistory(nextHistory);
    setInput('');
    setLoading(true);
    setError('');

    try {
      const data = await adminAiReply({
        messages: nextHistory.map(({ role, content }) => ({ role, content })),
      });
      setHistory(h => [...h, {
        role: 'assistant',
        content: data.reply || '',
        meta: {
          model: data.model,
          catalog_size: data.catalog_size,
          catalog_total: data.catalog_total,
          input_token_cap: data.input_token_cap,
          messages_dropped: data.messages_dropped,
          usage: data.usage,
          cost: data.cost,
        },
      }]);
      if (data.cost) {
        setTotalCost(prev => ({
          usd: +(prev.usd + (data.cost.usd || 0)).toFixed(6),
          pen: +(prev.pen + (data.cost.pen || 0)).toFixed(4),
        }));
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Error desconocido';
      setError(detail);
      // Rollback: quita el mensaje optimista del cliente si falló
      setHistory(h => h.slice(0, -1));
      setInput(text);
    } finally {
      setLoading(false);
      taRef.current?.focus();
    }
  };

  const resetChat = () => {
    setHistory([]);
    setInput('');
    setError('');
    setTotalCost({ usd: 0, pen: 0 });
    taRef.current?.focus();
  };

  const onKey = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') send();
  };

  return (
    <div className="flex flex-col h-[calc(100vh-1px)] p-4 md:p-6 max-w-5xl mx-auto w-full">

      {/* Header */}
      <div className="mb-4 flex items-start justify-between gap-4 flex-wrap shrink-0">
        <div>
          <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-brand-faint mb-1">
            Administración
          </p>
          <h1 className="font-display text-2xl font-semibold text-brand-text tracking-tight flex items-center gap-2 flex-wrap">
            <Sparkles size={20} className="text-brand-accent" />
            Asistente IA
            <span
              className="inline-flex items-center gap-1.5 px-2 py-0.5 border border-brand-accent/40 bg-brand-accent/10 font-mono text-[10px] tracking-[0.08em] text-brand-accent normal-case"
              title={info?.model || 'cargando…'}
            >
              <Cpu size={11} strokeWidth={1.8} />
              {prettyModel(info?.model)}
              {info && !info.configured && (
                <span className="ml-1 text-brand-red">· sin API key</span>
              )}
            </span>
          </h1>
          <p className="text-[12px] text-brand-muted mt-1 max-w-2xl">
            Chat con memoria del hilo. Pega cada mensaje del cliente como llega; la
            IA recuerda el contexto y arma la respuesta. Cache 1h sobre el catálogo
            → del 2do mensaje en adelante casi gratis.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {totalCost.usd > 0 && (
            <span className="font-mono text-[10px] text-brand-faint">
              total chat: <span className="text-brand-accent">${totalCost.usd.toFixed(5)}</span>
              {' · '}
              <span>S/{totalCost.pen.toFixed(4)}</span>
            </span>
          )}
          <button
            onClick={resetChat}
            disabled={loading || history.length === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.12em] border border-brand-border-md text-brand-faint hover:border-brand-text hover:text-brand-muted disabled:opacity-30 transition-colors"
          >
            <Eraser size={12} /> Nueva conversación
          </button>
        </div>
      </div>

      {/* Chat area */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto border border-brand-border bg-brand-panel p-4 mb-3 space-y-4"
      >
        {history.length === 0 && !loading && (
          <div className="flex flex-col items-center justify-center h-full text-center gap-3 py-12">
            <Bot size={36} className="text-brand-faint" strokeWidth={1.4} />
            <p className="text-[13px] text-brand-muted max-w-md">
              Pega el primer mensaje del cliente y la IA te arma la respuesta.
              Después puedes seguir pegando lo que te vaya escribiendo y la IA
              mantiene contexto del hilo.
            </p>
            <div className="flex gap-1.5 flex-wrap justify-center max-w-2xl mt-2">
              {EXAMPLES.map(ex => (
                <button
                  key={ex}
                  onClick={() => setInput(ex)}
                  className="px-2.5 py-1 font-mono text-[10px] tracking-[0.04em] border border-brand-border-md text-brand-faint hover:border-brand-accent hover:text-brand-accent transition-colors"
                >
                  {ex}
                </button>
              ))}
            </div>
          </div>
        )}

        {history.map((msg, i) => (
          <div key={i} className={`flex gap-3 ${msg.role === 'user' ? '' : 'flex-row-reverse'}`}>
            <div className={`flex-shrink-0 w-8 h-8 flex items-center justify-center border ${
              msg.role === 'user'
                ? 'border-brand-border-md text-brand-muted bg-brand-surface'
                : 'border-brand-accent/40 text-brand-accent bg-brand-accent/10'
            }`}>
              {msg.role === 'user' ? <User size={14} /> : <Sparkles size={14} />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-brand-faint">
                  {msg.role === 'user' ? 'Cliente' : 'Sugerencia para responder'}
                </span>
                {msg.role === 'assistant' && msg.content && <CopyButton text={msg.content} />}
              </div>
              <pre className={`whitespace-pre-wrap font-sans text-[14px] leading-relaxed px-3 py-2.5 border ${
                msg.role === 'user'
                  ? 'border-brand-border bg-brand-surface text-brand-muted'
                  : 'border-brand-accent/30 bg-brand-accent/[0.04] text-brand-text'
              }`}>
                {msg.content}
              </pre>
              {msg.role === 'assistant' && msg.meta && (
                <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 font-mono text-[9px] text-brand-faint">
                  <span>in: {msg.meta.usage?.input_tokens}t</span>
                  <span>out: {msg.meta.usage?.output_tokens}t</span>
                  {msg.meta.usage?.cache_creation_input_tokens > 0 && (
                    <span className="text-yellow-400">
                      cache write: {msg.meta.usage.cache_creation_input_tokens}t
                    </span>
                  )}
                  {msg.meta.usage?.cache_read_input_tokens > 0 && (
                    <span className="text-brand-accent">
                      cache hit: {msg.meta.usage.cache_read_input_tokens}t
                    </span>
                  )}
                  {msg.meta.catalog_total > msg.meta.catalog_size && (
                    <span className="text-yellow-400" title={`Recortado por cap de ${msg.meta.input_token_cap}t`}>
                      catálogo: {msg.meta.catalog_size}/{msg.meta.catalog_total} (recortado)
                    </span>
                  )}
                  {msg.meta.messages_dropped > 0 && (
                    <span className="text-yellow-400">
                      -{msg.meta.messages_dropped} msg viejos
                    </span>
                  )}
                  {msg.meta.cost && (
                    <span className="text-brand-muted">
                      · ${msg.meta.cost.usd?.toFixed(5)} · S/{msg.meta.cost.pen?.toFixed(4)}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex gap-3 flex-row-reverse">
            <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center border border-brand-accent/40 text-brand-accent bg-brand-accent/10">
              <RefreshCcw size={14} className="animate-spin" />
            </div>
            <div className="flex-1">
              <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-brand-faint">
                Pensando…
              </span>
            </div>
          </div>
        )}

        {error && (
          <div className="border border-brand-red/40 bg-brand-red/10 px-3 py-2 text-[13px] text-brand-red">
            {error}
          </div>
        )}
      </div>

      {/* Input */}
      <div className="border border-brand-border bg-brand-panel shrink-0">
        <div className="px-4 py-2 border-b border-brand-border flex items-center justify-between">
          <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-brand-faint">
            {history.length === 0 ? 'Primer mensaje del cliente' : 'Siguiente mensaje del cliente'}
          </span>
          <span className="font-mono text-[10px] text-brand-faint">
            Ctrl/⌘ + Enter
          </span>
        </div>
        <textarea
          ref={taRef}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={onKey}
          placeholder={history.length === 0
            ? 'Pega aquí el mensaje del cliente…'
            : 'Pega lo siguiente que escribió el cliente…'}
          rows={3}
          className="w-full bg-transparent px-4 py-3 text-[14px] text-brand-text placeholder:text-brand-faint focus:outline-none resize-none"
        />
        <div className="px-4 py-2 border-t border-brand-border flex items-center justify-end">
          <button
            onClick={send}
            disabled={loading || !input.trim()}
            className="flex items-center gap-2 px-4 py-2 bg-brand-accent text-brand-bg font-medium text-[13px] disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
          >
            {loading
              ? <><RefreshCcw size={14} className="animate-spin" /> Generando…</>
              : <><Send size={14} /> Generar respuesta</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AIAssistantPage;
